import React from "react";
import clsx from "clsx";

// Material UI Components
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Styles
import { useStyles } from "./CalculatorFAQ.styles";

const CalculatorFAQ = () => {
  const classes = useStyles();

  const FAQ = [
    {
      question: "What does my price include?",
      answer:
        "The vehicle price field should not include tax or additional fees. Dealerships will in many situations add additional fees on top of their advertised price. A dealership might call this price field the MSRP, the Manufacturer's Suggested Retail Price.",
    },
    {
      question: "What is down payment?",
      answer:
        "A down payment is money that you put toward your car purchase and that you don’t pay interest on. You would put a down payment on a vehicle at the time of purchase so that your lease or finance monthly payments become cheaper overall. The more of the car you pay upfront, the less you will have to pay back in car payments. You cannot put a down payment on a “cash purchase”. When purchasing a car with cash, you are simply paying the entire amount right away.",
    },
    {
      question: "What is the term on a vehicle?",
      answer:
        "A Term, or Term in Months as shown on the calculator above, is the length of time that you would like to have your car loan. The longer the term, the cheaper your payments will be, however, it means that you have that car for a longer period of time, and you will be paying more in interest over that total length of time.",
    },
    {
      question: "What term should your loan be?",
      answer:
        "Your loan term is a personal decision and depends on many factors, including your lifestyle, your income, how you like to budget, and how long you would like to own your vehicle. The loan amount will fluctuate to a degree depending on which loan term and downpayment you choose. We recommend you talk to one of our Vehicle Experts if you want to discuss loan terms and your car loan payments. They can give you guidance on making the decision that’s right for you.",
    },
    {
      question: "What is APR on a car?",
      answer: "APR stands for Annual Percentage Rate.",
    },
    {
      question: "Do I pay interest on a car?",
      answer:
        "If you have leased or financed a vehicle you will pay interest on the debt that remains before you fully own it. To recap, interest is a fee alongside your car payments that goes to the lender from where you are borrowing money.",
    },
    {
      question: "What is provincial vehicle sales tax?",
      answer:
        "Every province has a different tax rate when purchasing a vehicle. Depending on your province, this will include PST, GST or HST. BC has a luxury vehicle tax which is an additional tax on vehicles that are priced over a certain amount. See our luxury vehicle tax blog post for more details on this. The Federal government also has an additional luxury sales tax on top of the provincial. For private sale vehicles, some provinces will still require you to pay tax on the car even though it is a used purchase. If you try and get around this, by let’s say pricing your vehicle at $1, the Province will deny your vehicle ownership transfer and expect a reasonable vehicle sales amount for your make model and year.",
    },
    {
      question: "What are additional fees on a car?",
      answer:
        "Additional fees can include many things, such as freight (the cost to ship a car), pre-delivery inspection, and administration fees. Some of these fees are mandatory, and some of them optional - it’s always a good idea to inquire as to what these fees are. Additional fees are pre-tax.",
    },
    {
      question: "What is leasing a car?",
      answer:
        "Leasing a car means that you don’t own the car, you pay to use it for a fixed period of time (your loan term). You can only lease a new vehicle. You can only lease from a dealership. When the lease term is up, you will return the car to the dealership or in some situations you can buy out the remainder of the owned value of the car and at that point, you would own the vehicle. An advantage of leasing is that you only pay the tax on the amount you pay each month - this means you do not have to pay luxury tax on a leased vehicle.",
    },
    {
      question: "What is financing a car?",
      answer:
        "Financing is similar to leasing in that you pay in monthly instalments, but you are taking out a loan and paying back that loan over time. You have taken ownership of the vehicle. You can finance a new car or a used car. You can shop around for loan offers - banks will provide loan options and so will most dealerships.",
    },
    {
      question: "What is paying cash for a car?",
      answer:
        "It’s pretty simple, you will pay the total amount for the car up front at the time of purchase, including taxes and any additional fees. You will have full ownership of the car.",
    },
    {
      question: "What does my total payment mean?",
      answer:
        "After all additional fees, taxes, etc., are added up, the calculator above will show the total payment that you would have to make. Your payment frequency can either be biweekly or semi-monthly payments. Bi-weekly has 26 payments per year, and semi-monthly has 24 payments per year. Reminder: this calculator is an estimate, and your total payment will be determined when you visit a dealership.",
    },
    {
      question: "How do you pay?",
      answer:
        "Your biweekly or semi-monthly car payment will be taken out of your bank account directly. You can also pay via your credit card. The payment will be taken on the contract date, but can usually be changed after the first payment.",
    },
  ];

  const renderOverview = () => (
    <>
      <Typography className={classes.title}>Overview</Typography>
      <Typography display="inline" className={classes.content}>
        Purchasing a car can be complicated. VINN has built a payment calculator
        to help you walk through a potential purchase, step by step, so you are
        able to see an average of what your total vehicle costs may look like.
        Our payment calculator is a good overview of what you can expect from
        purchasing at a dealership, but if you would like more in-depth
        assistance, speak to one of our Vehicle Experts. They help thousands of
        customers a month free of charge.
      </Typography>
      <Typography display="inline" className={classes.superscript}>
        1 2
      </Typography>
      <div className={classes.gap} />
      <Typography display="inline" className={classes.superscript}>
        1
      </Typography>
      <Typography
        display="inline"
        className={clsx(classes.content, classes.startSpace)}
      >
        This is for informational purposes only and may change based on the
        dealership or other retailer that you are purchasing from.
      </Typography>
      <div className={classes.gap} />
      <Typography display="inline" className={classes.superscript}>
        2
      </Typography>
      <Typography
        display="inline"
        className={clsx(classes.content, classes.startSpace)}
      >
        We use the term finance as one of the tabs, if you are looking for a
        loan calculator, this is the same thing.
      </Typography>
      <div className={classes.gap} />
      <Typography className={classes.content}>
        If you need guidance on how to use the calculator, we have some
        definitions and answers to common questions below.
      </Typography>
    </>
  );

  return (
    <div className={classes.root}>
      {renderOverview()}
      <Typography className={classes.title}>
        Frequently Asked Questions
      </Typography>
      <div className={classes.gap} />
      {FAQ.map((item, index) => (
        <Accordion
          key={item.question}
          elevation={0}
          classes={{ root: classes.noContent, expanded: classes.expanded }}
          aria-controls={`FAQ-content-${index}`}
        >
          <AccordionSummary
            className={classes.accordion}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography className={classes.heading} id={`FAQ-content-${index}`}>
              {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography className={classes.content}>{item.answer}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default CalculatorFAQ;
